import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["scanInput", "jobDisplay", "noJobMessage"]

  connect() {
    // Auto-focus the scan input when page loads
    this.scanInputTarget.focus()
    // Track focused item for bin validation
    this.focusedItem = null
  }

  scan(event) {
    event.preventDefault()

    const scanInput = this.scanInputTarget.value.trim()

    if (!scanInput) {
      return
    }

    // Clear the input for next scan
    const inputValue = scanInput
    this.scanInputTarget.value = ''

    // Check if this is a bin scan (and we have a focused item)
    if (inputValue.startsWith('BIN:') && this.focusedItem) {
      this.handleBinScan(inputValue)
      return
    }

    // Otherwise, process as item/order scan
    fetch('/picking_station/scan', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({ scan_input: inputValue })
    })
    .then(response => response.json())
    .then(data => {
      if (data.error) {
        this.showError(data.error)
      } else {
        // Handle different scan types
        if (data.scan_type === 'item') {
          this.displayFocusedItem(data)
        } else {
          // Order scan - full display
          this.displayJob(data.job)
        }
      }
      // Re-focus input for next scan
      this.scanInputTarget.focus()
    })
    .catch(error => {
      console.error('Error:', error)
      this.showError('An error occurred while scanning')
      this.scanInputTarget.focus()
    })
  }

  handleBinScan(binBarcode) {
    if (!this.focusedItem) {
      this.showError('No focused item. Scan an item barcode first.')
      return
    }

    // Parse bin barcode: BIN:SKU:COLOR:SIZE
    const parts = binBarcode.replace('BIN:', '').split(':')
    if (parts.length !== 3) {
      this.showError('Invalid bin barcode format. Expected: BIN:SKU:COLOR:SIZE')
      this.scanInputTarget.focus()
      return
    }

    const binData = {
      sku: parts[0].trim(),
      color: parts[1].trim().toUpperCase(),
      size: parts[2].trim().toUpperCase()
    }

    // Validate the pick
    fetch('/picking_station/validate_pick', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({
        item_id: this.focusedItem.id,
        bin_sku: binData.sku,
        bin_color: binData.color,
        bin_size: binData.size
      })
    })
    .then(response => response.json())
    .then(data => {
      if (data.match) {
        // Success! Show success message and clear for next pick
        this.showSuccess(data.message)
        this.focusedItem = null
        this.noJobMessageTarget.classList.remove('hidden')
        this.jobDisplayTarget.classList.add('hidden')
      } else {
        // Mismatch! Show detailed error
        let errorMessage = data.error + '\n\nMismatches:\n'
        errorMessage += data.mismatches.join('\n')
        this.showError(errorMessage)
      }
      this.scanInputTarget.focus()
    })
    .catch(error => {
      console.error('Error:', error)
      this.showError('An error occurred while validating the pick')
      this.scanInputTarget.focus()
    })
  }

  displayFocusedItem(data) {
    this.noJobMessageTarget.classList.add('hidden')
    this.jobDisplayTarget.classList.remove('hidden')

    // Store first focused item for bin validation
    this.focusedItem = data.focused_items[0]

    const isMultiLocation = data.focused_items.length > 1

    const html = `
      <div class="bg-white shadow sm:rounded-lg mb-6">
        <div class="px-4 py-5 sm:px-6">
          <h3 class="text-2xl font-semibold text-gray-900">Order #${data.job.ordernumber}</h3>
          <p class="mt-2 text-sm text-gray-500">Item ready for picking - Scan bin label to verify</p>
          <div class="mt-4 bg-green-50 border border-green-200 rounded-md p-4">
            <p class="text-green-800 font-medium text-center text-lg">
              📦 Scan bin label: BIN:${this.focusedItem.product}:${this.focusedItem.color}:${this.focusedItem.size}
            </p>
          </div>
        </div>
      </div>

      <div class="overflow-x-auto shadow ring-1 ring-black/5 sm:rounded-lg">
        <table class="min-w-full divide-y divide-gray-300">
          <thead class="bg-gray-50">
            <tr>
              <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6" style="min-width: 200px;">Preview</th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Item ID</th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Product</th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Color</th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Size</th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Location</th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 bg-white">
            ${this.renderFocusedItems(data.focused_items, isMultiLocation)}
          </tbody>
        </table>
      </div>
    `

    this.jobDisplayTarget.innerHTML = html
  }

  renderFocusedItems(items, isMultiLocation) {
    let html = ''
    items.forEach((item, index) => {
      const isFirst = index === 0
      const borderClass = isMultiLocation ? 'border-l-4 border-l-green-500' : ''
      const bgClass = item.is_reprint ? 'bg-orange-50' : (isMultiLocation ? 'bg-green-50' : 'bg-white')
      const itemIdDisplay = isFirst ? (item.order_item_id || '-') : ''
      const groupIndicator = isMultiLocation && isFirst ?
        '<span class="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 ml-2">Multi-location group</span>' : ''
      const reprintBadge = item.is_reprint ?
        '<span class="inline-flex items-center gap-x-1.5 rounded-full px-2 py-1 text-xs font-medium text-orange-700 bg-orange-100 ring-1 ring-inset ring-orange-600/20"><svg class="h-1.5 w-1.5 fill-orange-500" viewBox="0 0 6 6"><circle cx="3" cy="3" r="3" /></svg>REPRINT</span>' : ''

      const thumbnailHTML = item.thumbnail_url ?
        `<img src="${item.thumbnail_url}" alt="Print preview" style="width: 200px; min-width: 200px; height: auto;" class="object-contain rounded border border-gray-300 bg-gray-200">` :
        `<div style="width: 200px; min-width: 200px; height: 229px;" class="flex items-center justify-center bg-gray-100 rounded border border-gray-300 text-gray-400 text-sm">No image</div>`

      html += `
        <tr class="${borderClass} ${bgClass}">
          <td class="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6" style="min-width: 200px;">
            ${thumbnailHTML}
          </td>
          <td class="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">
            ${itemIdDisplay}${groupIndicator}
          </td>
          <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
            <div class="flex items-center gap-2">
              ${item.product || '-'}
              ${reprintBadge}
            </div>
          </td>
          <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">${item.color || '-'}</td>
          <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">${item.size || '-'}</td>
          <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">${item.print_location || '-'}</td>
          <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
            ${this.statusBadgeHTML(item.status, item.is_reprint)}
          </td>
        </tr>
      `
    })
    return html
  }

  displayJob(job) {
    this.noJobMessageTarget.classList.add('hidden')
    this.jobDisplayTarget.classList.remove('hidden')
    this.focusedItem = null

    const html = `
      <div class="bg-white shadow sm:rounded-lg mb-6">
        <div class="px-4 py-5 sm:px-6">
          <h3 class="text-2xl font-semibold text-gray-900">Order #${job.ordernumber}</h3>
          <div class="mt-2 grid grid-cols-2 gap-4">
            <div>
              <p class="text-sm text-gray-500">Production Date</p>
              <p class="text-sm font-medium text-gray-900">${job.production_date || 'Not set'}</p>
            </div>
            <div>
              <p class="text-sm text-gray-500">Job Status</p>
              <p class="text-sm font-medium text-gray-900">${job.status}</p>
            </div>
          </div>
          <div class="mt-4 bg-yellow-50 border border-yellow-200 rounded-md p-3">
            <p class="text-yellow-800 text-sm">
              💡 Scan an individual item barcode to pick and verify
            </p>
          </div>
        </div>
      </div>

      <div class="overflow-x-auto shadow ring-1 ring-black/5 sm:rounded-lg">
        <table class="min-w-full divide-y divide-gray-300">
          <thead class="bg-gray-50">
            <tr>
              <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6" style="min-width: 200px;">Preview</th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Item ID</th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Product</th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Color</th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Size</th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Location</th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 bg-white">
            ${this.renderAllItems(job.items)}
          </tbody>
        </table>
      </div>
    `

    this.jobDisplayTarget.innerHTML = html
  }

  renderAllItems(items) {
    let html = ''
    items.forEach(item => {
      const bgClass = item.is_reprint ? 'bg-orange-50' : 'bg-white'
      const reprintBadge = item.is_reprint ?
        '<span class="inline-flex items-center gap-x-1.5 rounded-full px-2 py-1 text-xs font-medium text-orange-700 bg-orange-100 ring-1 ring-inset ring-orange-600/20"><svg class="h-1.5 w-1.5 fill-orange-500" viewBox="0 0 6 6"><circle cx="3" cy="3" r="3" /></svg>REPRINT</span>' : ''

      const thumbnailHTML = item.thumbnail_url ?
        `<img src="${item.thumbnail_url}" alt="Print preview" style="width: 200px; min-width: 200px; height: auto;" class="object-contain rounded border border-gray-300 bg-gray-200">` :
        `<div style="width: 200px; min-width: 200px; height: 229px;" class="flex items-center justify-center bg-gray-100 rounded border border-gray-300 text-gray-400 text-sm">No image</div>`

      html += `
        <tr class="${bgClass}">
          <td class="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6" style="min-width: 200px;">
            ${thumbnailHTML}
          </td>
          <td class="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">${item.order_item_id || '-'}</td>
          <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
            <div class="flex items-center gap-2">
              ${item.product || '-'}
              ${reprintBadge}
            </div>
          </td>
          <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">${item.color || '-'}</td>
          <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">${item.size || '-'}</td>
          <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">${item.print_location || '-'}</td>
          <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
            ${this.statusBadgeHTML(item.status, item.is_reprint)}
          </td>
        </tr>
      `
    })
    return html
  }

  statusBadgeHTML(status, isReprint = false) {
    const badgeClass = this.getStatusBadgeClass(status, isReprint)
    return `<span class="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${badgeClass}">${status}</span>`
  }

  getStatusBadgeClass(status, isReprint = false) {
    // Override with orange styling for reprints
    if (isReprint) return 'bg-orange-50 text-orange-700 ring-orange-600/20'

    if (status.includes('Pending')) return 'bg-yellow-50 text-yellow-700 ring-yellow-600/20'
    if (status.includes('Picking')) return 'bg-blue-50 text-blue-700 ring-blue-600/20'
    if (status.includes('Complete') || status.includes('Pass')) return 'bg-green-50 text-green-700 ring-green-600/20'
    if (status.includes('Fail')) return 'bg-red-50 text-red-700 ring-red-600/20'
    return 'bg-gray-50 text-gray-700 ring-gray-600/20'
  }

  showSuccess(message) {
    const successDiv = document.createElement('div')
    successDiv.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-4 rounded-lg shadow-lg z-50 text-lg'
    successDiv.textContent = message
    document.body.appendChild(successDiv)

    setTimeout(() => {
      successDiv.remove()
    }, 3000)
  }

  showError(message) {
    this.jobDisplayTarget.classList.add('hidden')
    this.noJobMessageTarget.classList.remove('hidden')
    this.noJobMessageTarget.innerHTML = `
      <div class="rounded-md bg-red-50 p-4">
        <div class="flex">
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800">Error</h3>
            <div class="mt-2 text-sm text-red-700">
              <p style="white-space: pre-line;">${message}</p>
            </div>
          </div>
        </div>
      </div>
    `
  }
}
